<template>
	<article class="project-list-item p-2 alternate-bg">
		<a
			v-b-toggle
			class="d-flex header"
			:href="`#project-detail-${project.id}`"
			@click.prevent
		>
			<div class="header-text" @click="$emit('project-selected', project)">
				<h5 class="project-name m-0">
					{{ project.name }}
				</h5>
				<div class="project-location text-muted">
					{{ project.location }}
				</div>
			</div>
		</a>
		<b-collapse :id="`project-detail-${project.id}`">
			<p class="my-2 text-primary">
				<i><u>{{ project.tagline }}</u></i>
			</p>
			<p class="description">
				{{ project.description }}
			</p>
			<section class="scenarios">
				<h6>
					{{ $t('projectlist.scenarios-heading') }}
				</h6>
				<b-list-group>
					<b-list-group-item
						v-for="scenario in project.scenarios"
						:key="scenario.id"
						class="alternate-bg p-0"
					>
						<div class="d-flex justify-content-between">
							<template v-if="scenario.id === renameScenarioId">
								<b-form-input
									:value="scenario.name"
									@input="newScenarioName = $event"
									@keyup.enter.native="renameScenario(scenario, newScenarioName)"
								/>
								<b-button
									v-confirm="[$t('projectlist.rename-scenario.modalOptions'),() => { renameScenario(scenario, newScenarioName) }]"
									size="sm"
								>
									<b-icon icon="check" />
								</b-button>
								<b-button
									size="sm"
									variant="dark"
									@click="renameScenarioId = null"
								>
									<b-icon icon="x" />
								</b-button>
							</template>
							<template v-else>
								<router-link v-slot="{ navigate }" custom :to="`/scenarios/${scenario.id}`">
									<span class="scenario-name" role="button" @click="navigate">
										<b-icon-lock v-show="scenario.locked" />
										{{ scenario.name }}
									</span>
								</router-link>
								<b-dropdown size="sm" variant="default" no-caret>
									<template #button-content>
										<b-icon icon="three-dots" />
									</template>
									<b-dropdown-item
										v-if="userHasManagerPrivileges"
										to="#scenario-lock"
										@click="toggleScenarioLock(scenario)"
									>
										<template v-if="scenario.locked">
											{{ $t('projectlist.unlock-scenario') }}
										</template>
										<template v-else>
											{{ $t('projectlist.lock-scenario') }}
										</template>
									</b-dropdown-item>
									<b-dropdown-item to="#scenario-rename" @click="renameScenarioId = scenario.id">
										{{ $t('projectlist.rename-scenario.rename') }}
									</b-dropdown-item>
									<b-dropdown-item
										v-confirm="[$t('projectlist.archive-scenario.modalOptions'),() => { archiveScenario(scenario.id) }]"
										to="#scenario-archive"
									>
										{{ $t('projectlist.archive-scenario.archive') }}
									</b-dropdown-item>
								</b-dropdown>
							</template>
						</div>
					</b-list-group-item>
					<b-list-group-item
						v-show="showNewScenarioForm"
						class="alternate-bg"
					>
						<b-form-group
							:label="$t('projectlist.base-scenario-on')"
							label-for="scenario-source"
						>
							<b-select id="scenario-source" v-model="newScenarioData.source">
								<b-select-option value="">
									{{ $t("empty-scenario") }}
								</b-select-option>
								<b-select-option
									v-for="scenario in project.scenarios"
									:key="scenario.id"
									:value="scenario.id"
								>
									{{ scenario.name }}
								</b-select-option>
							</b-select>
						</b-form-group>
						<b-form-group
							:label="$t('scenario-name')"
							label-for="scenario-name"
						>
							<b-form-input
								id="scenario-name"
								v-model="newScenarioData.name"
								required
								@keydown.enter="createScenario(newScenarioData)"
							/>
						</b-form-group>
					</b-list-group-item>
				</b-list-group>
				<b-button
					v-if="!showNewScenarioForm"
					class="w-100"
					size="sm"
					@click="showNewScenarioForm=true"
				>
					{{ $t('projectlist.add-scenario') }}
				</b-button>
				<template v-else>
					<b-button-group class="w-100">
						<b-button variant="dark" class="text-capitalize" @click="showNewScenarioForm = false">
							{{ $t("projectlist.create-scenario.cancel") }}
						</b-button>
						<b-button class="text-capitalize" @click="createScenario(newScenarioData)">
							{{ $t("projectlist.create-scenario.create") }}
						</b-button>
					</b-button-group>
				</template>
			</section>
		</b-collapse>
	</article>
</template>

<script>
export default {
	name: 'ProjectListItem',
	props: {
		project: {
			type: Object,
			required: true,
		},
		index: {
			type: Number,
			required: true,
		},
	},
	data() {
		return {
			renameScenarioId: null,
			newScenarioName: null,
			showNewScenarioForm: false,
			newScenarioData: {
				name: '',
				source: '',
			},
		};
	},
	methods: {
		makeErrorToast(title, error) {
			this.$bvToast.toast(`${error}`, {
				title,
				autoHideDelay: 5000,
				appendToast: false,
				variant: 'danger',
			});
		},
		async createScenario(scenarioData) {
			try {
				await this.axios.post(`/projects/${this.project.id}/scenarios/`, scenarioData);
				this.showNewScenarioForm = false;
				this.$emit('project-mutated');
			} catch (error) {
				this.makeErrorToast(this.$t('projectlist.create-scenario-error'), `${error}`);
			}
		},
		async toggleScenarioLock(scenario) {
			try {
				await this.axios.patch(`/scenarios/${scenario.id}`, { locked: !scenario.locked });
				await this.$emit('project-mutated');
			} catch (error) {
				this.makeErrorToast(this.$t('projectlist.update-scenario-error'), `${error}`);
			}
		},
		async renameScenario(scenario, newName) {
			if (newName === null || !/\w+/.test(newName)) {
				this.renameScenarioId = null;
				return;
			}
			try {
				await this.axios.patch(`/scenarios/${scenario.id}`, { name: newName });
				await this.$emit('project-mutated');
				// set this variable at the end so input field stays until new data from backend arrived
				this.renameScenarioId = null;
			} catch (error) {
				this.renameScenarioId = null;
				this.makeErrorToast(this.$t('projectlist.update-scenario-error'), `${error}`);
			}
		},
		async archiveScenario(id) {
			try {
				await this.axios.delete(`/scenarios/${id}`);
				await this.$emit('project-mutated');
			} catch (err) {
				this.makeErrorToast(err, `${err}`);
			}
		},
		userHasManagerPrivileges() {
			if (this.project.project_user && this.project.project_user.isProjectManager) {
				return true;
			}
			if (['OWNER', 'ADMIN'].includes(this.$store.state.user.currentEnterprise.role)) {
				return true;
			}
			return false;
		},
	},
};
</script>

<style lang="scss" scoped>
.header {
	text-decoration: none !important;
	.header-text {
		width: 100%;
	}
}

.description {
	font-size: 0.9em;
}

.scenario-name {
	line-height: 1.9;
	padding: 0.3em 0 0.3em 0.5em;
}
</style>
